import * as React from 'react';
import { styled } from '@glitz/react';
import { SlideHeightAnimation, createFlipYDecorator } from '@avensia/excite-ui';
import {
  createMaxMobileViewportDecorator,
  createMaxSmallViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { secondaryGrey } from 'webapp/shared/sharedTheme';
import { ChevronDownIcon } from 'webapp/components/icons';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';

export function ExpandableBox({
  header,
  children,
  openAccordion,
  setOpenAccordion,
  className,
  contentPadding = true,
  id,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
  openAccordion?: string;
  setOpenAccordion: (id: undefined | string) => void;
  className?: string;
  contentPadding?: boolean;
  id: string;
}) {
  const onToggle = React.useCallback(() => {
    if (openAccordion === id) {
      setOpenAccordion(undefined);
    } else {
      setOpenAccordion(id);
    }
  }, [id, openAccordion, setOpenAccordion]);

  return (
    <Base className={className}>
      <Header
        as={Theme.Transparent}
        disableRippleEffect
        onClick={() => onToggle()}
        aria-expanded={openAccordion === id}
      >
        <HeaderText>{header}</HeaderText>
        <ArrowUp css={createFlipYDecorator(openAccordion === id)} />
      </Header>
      <HeightAnimation
        keepElement
        visible={openAccordion === id}
        css={styled(contentPadding && paddingDecorator, {
          visibility: openAccordion === id ? 'visible' : 'hidden', //To ensure we don't get focus when not visible
        })}
      >
        {children}
      </HeightAnimation>
    </Base>
  );
}

const Base = styled.div(
  createMaxMobileViewportDecorator({
    padding: { x: t => t.sharedSpacing.m },
  }),
);

const Header = styled(
  Button,
  createButtonHoverDecorator({
    borderTop: {
      color: secondaryGrey,
    },
  }),
  createMinMediumViewportDecorator({
    padding: { x: t => t.sharedSpacing.xl },
  }),
  {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
    borderTop: {
      width: 'thin',
      style: 'solid',
      color: secondaryGrey,
    },
  },
);

const HeaderText = styled.h2({
  fontSize: t => t.sharedFontSize.m,
  marginBottom: 0,
  width: '100%',
  display: 'flex',
  lineHeight: 'normal',
  alignItems: 'center',
  fontWeight: 400,
  padding: { y: t => t.sharedSpacing.m },
});

const IconStyle = styled({
  fontSize: t => t.sharedFontSize.m,
  strokeWidth: 1.3,
});

const ArrowUp = styled(ChevronDownIcon, IconStyle, {
  zIndex: -1,
});

const HeightAnimation = styled(
  SlideHeightAnimation,
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.xs,
  }),
  {
    fontSize: t => t.sharedFontSize.s,
    paddingTop: t => t.sharedSpacing.m,
  },
);

const paddingDecorator = styled(
  createMaxMobileViewportDecorator({
    padding: { x: t => t.sharedSpacing.m, bottom: t => t.sharedSpacing.xxl },
  }),
  {
    padding: { x: t => t.sharedSpacing.xl, bottom: t => t.sharedSpacing.xxxxl },
  },
);
